import React, { useState, useEffect } from "react";
import { safeScrollToTop } from "../utils/scrollGuard";
import {
  FaShare,
  FaGift,
  FaCopy,
  FaUsers,
  FaTrophy,
  FaStar,
  FaCheckCircle,
  FaFacebookF,
  FaTwitter,
  FaWhatsapp,
  FaEnvelope,
} from "react-icons/fa";
import { toast } from "react-toastify";
import jwtTokenService from "../services/jwtTokenService";
import rewardApi from "../api/rewardApi";
import { APP_URLS } from "../config/urls.config";

const ReferAndEarn = () => {
  const [referralsCount, setReferralsCount] = useState(0);
  const referralsNeeded = 5;
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  useEffect(() => {
    const user = jwtTokenService.getUser();
    const userId = user?.id || user?.user_id || user?.userId;
    if (userId) {
      const code = "TRV" + String(userId).padStart(6, "0");
      setReferralCode(code);
      rewardApi.getUserRewardPoints(userId)
        .then((data) => {
          if (data?.totalBookings !== undefined) {
            setReferralsCount(data.totalBookings);
          }
        })
        .catch(() => {});
    }
  }, []);

  const copyToClipboard = () => {
    const referralLink = referralCode
      ? `${APP_URLS.REFER}/${referralCode}`
      : APP_URLS.REFER;
    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setCopied(true);
        toast.success("Referral link copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
        toast.error("Failed to copy referral link");
      });
  };

  const steps = [
    {
      number: "1",
      title: "Share Your Link",
      description: "Copy and share your unique referral link with friends",
    },
    {
      number: "2",
      title: "Friend Books",
      description: "They use your link to make their first booking",
    },
    {
      number: "3",
      title: "Earn Rewards",
      description: "You both receive instant rewards on completion",
    },
  ];

  const rewards = [
    {
      icon: FaTrophy,
      tier: "Bronze",
      referrals: "1+",
      reward: "10% discount on next booking",
      color: "#CD7F32",
    },
    {
      icon: FaStar,
      tier: "Silver",
      referrals: "5+",
      reward: "₹1000 cash voucher",
      color: "#C0C0C0",
    },
    {
      icon: FaGift,
      tier: "Gold",
      referrals: "10+",
      reward: "Free night stay at partner hotel",
      color: "#FFD700",
    },
  ];

  const benefits = [
    "Unlimited earning potential",
    "Instant reward activation",
    "Friends get welcome discount",
    "Track all referrals in real-time",
  ];

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Hero Section with Gradient */}
      <section
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
          padding: "50px 0",
        }}
      >
        <div className="container mx-auto px-4 text-center text-white relative z-10">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "rgba(255, 255, 255, 0.2)",
              padding: "6px 16px",
              borderRadius: "20px",
              marginBottom: "12px",
              fontSize: "0.8rem",
              fontWeight: "600",
            }}
          >
            <FaGift style={{ fontSize: "0.85rem" }} />
            Rewards Program
          </div>
          <h1
            style={{
              fontSize: "clamp(1.75rem, 4.5vw, 2.75rem)",
              fontWeight: "800",
              letterSpacing: "-1.2px",
              marginBottom: "10px",
              textShadow: "0 2px 20px rgba(0,0,0,0.2)",
              lineHeight: "1.1",
            }}
          >
            Refer Friends, Earn Rewards
          </h1>
          <p
            style={{
              fontSize: "clamp(0.9rem, 1.8vw, 1.05rem)",
              maxWidth: "600px",
              margin: "0 auto 28px",
              opacity: 0.95,
              fontWeight: "500",
              lineHeight: "1.5",
            }}
          >
            Share the joy of seamless bookings with your friends and get
            rewarded for every successful referral
          </p>

          {/* Referral Link Box */}
          <div
            className="max-w-2xl mx-auto bg-white rounded-xl p-4"
            style={{
              boxShadow: "0 15px 40px rgba(0,0,0,0.25)",
            }}
          >
            <p
              style={{
                color: "#2E2E2E",
                fontWeight: "700",
                fontSize: "0.85rem",
                marginBottom: "10px",
                letterSpacing: "-0.3px",
              }}
            >
              Your Unique Referral Link
            </p>
            <div
              className="flex flex-col md:flex-row gap-2 items-stretch"
              style={{
                background: "#F8F9FA",
                padding: "6px",
                borderRadius: "10px",
              }}
            >
              <input
                type="text"
                value={referralCode ? `${APP_URLS.REFER}/${referralCode}` : APP_URLS.REFER}
                readOnly
                style={{
                  flex: 1,
                  padding: "11px 14px",
                  border: "2px solid #E5E7EB",
                  borderRadius: "7px",
                  fontSize: "0.875rem",
                  fontFamily: "'Inter', sans-serif",
                  color: "#2E2E2E",
                  fontWeight: "500",
                  background: "white",
                }}
              />
              <button
                onClick={copyToClipboard}
                style={{
                  padding: "11px 24px",
                  background: copied ? "#22C55E" : "#EE2E24",
                  color: "white",
                  border: "none",
                  borderRadius: "7px",
                  fontWeight: "700",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                  boxShadow: "0 3px 10px rgba(238, 46, 36, 0.3)",
                }}
                onMouseEnter={(e) => {
                  if (!copied) {
                    e.target.style.background = "#D82820";
                    e.target.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!copied) {
                    e.target.style.background = "#EE2E24";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                {copied ? (
                  <>
                    <FaCheckCircle style={{ fontSize: "0.9rem" }} /> Copied!
                  </>
                ) : (
                  <>
                    <FaCopy style={{ fontSize: "0.9rem" }} /> Copy Link
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div
          style={{
            position: "absolute",
            top: "20%",
            left: "-50px",
            width: "200px",
            height: "200px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            filter: "blur(60px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "20%",
            right: "-50px",
            width: "250px",
            height: "250px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            filter: "blur(60px)",
          }}
        />
      </section>

      {/* How It Works - Timeline Style */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <h2
            className="text-center mb-8"
            style={{
              fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
              fontWeight: "800",
              color: "#2E2E2E",
              letterSpacing: "-1px",
            }}
          >
            How It Works
          </h2>

          <div className="max-w-3xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-5 mb-6 last:mb-0">
                {/* Number Circle */}
                <div className="flex-shrink-0">
                  <div
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "1.3rem",
                      fontWeight: "800",
                      boxShadow: "0 3px 15px rgba(238, 46, 36, 0.3)",
                    }}
                  >
                    {step.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      style={{
                        width: "2px",
                        height: "50px",
                        background:
                          "linear-gradient(180deg, #EE2E24 0%, #FFC400 100%)",
                        margin: "6px auto",
                        opacity: 0.3,
                      }}
                    />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, paddingTop: "4px" }}>
                  <h3
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "800",
                      color: "#2E2E2E",
                      marginBottom: "4px",
                      letterSpacing: "-0.6px",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      color: "#64748B",
                      fontSize: "0.9rem",
                      lineHeight: "1.5",
                    }}
                  >
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Progress Tracker */}
      <section
        style={{
          background:
            "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
          padding: "40px 0",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2
              style={{
                fontSize: "clamp(1.35rem, 3vw, 1.75rem)",
                fontWeight: "800",
                color: "#2E2E2E",
                marginBottom: "8px",
                letterSpacing: "-0.9px",
              }}
            >
              Your Referral Progress
            </h2>
            <p
              style={{
                color: "#64748B",
                fontSize: "0.9rem",
                marginBottom: "24px",
              }}
            >
              You've referred{" "}
              <span style={{ color: "#EE2E24", fontWeight: "700" }}>
                {referralsCount}
              </span>{" "}
              out of{" "}
              <span style={{ color: "#EE2E24", fontWeight: "700" }}>
                {referralsNeeded}
              </span>{" "}
              friends for your next reward
            </p>

            {/* Progress Bar */}
            <div
              style={{
                width: "100%",
                height: "16px",
                background: "#E5E7EB",
                borderRadius: "100px",
                overflow: "hidden",
                position: "relative",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  width: `${(referralsCount / referralsNeeded) * 100}%`,
                  height: "100%",
                  background:
                    "linear-gradient(90deg, #EE2E24 0%, #FFC400 100%)",
                  borderRadius: "100px",
                  transition: "width 0.6s ease",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    animation: "shimmer 2s infinite",
                  }}
                />
              </div>
            </div>

            <p
              style={{
                marginTop: "12px",
                color: "#475569",
                fontSize: "0.85rem",
                fontWeight: "600",
              }}
            >
              {referralsCount === 0
                ? "Start referring to unlock rewards!"
                : `${referralsNeeded - referralsCount} more to go!`}
            </p>
          </div>
        </div>
      </section>

      {/* Reward Tiers */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <h2
            className="text-center mb-3"
            style={{
              fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
              fontWeight: "800",
              color: "#2E2E2E",
              letterSpacing: "-1px",
            }}
          >
            Unlock Exclusive Rewards
          </h2>
          <p
            className="text-center mb-8"
            style={{
              color: "#64748B",
              fontSize: "0.9rem",
              maxWidth: "550px",
              margin: "0 auto 32px",
            }}
          >
            The more friends you refer, the better the rewards get
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {rewards.map((reward, index) => {
              const IconComponent = reward.icon;
              return (
                <div
                  key={index}
                  className="text-center"
                  style={{
                    padding: "28px 20px",
                    borderRadius: "16px",
                    background: "white",
                    border: `2px solid ${reward.color}20`,
                    position: "relative",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = `0 15px 30px ${reward.color}30`;
                    e.currentTarget.style.borderColor = reward.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = `${reward.color}20`;
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: "65px",
                      height: "65px",
                      margin: "0 auto 14px",
                      borderRadius: "50%",
                      background: `${reward.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComponent
                      style={{ fontSize: "2rem", color: reward.color }}
                    />
                  </div>

                  <h3
                    style={{
                      fontSize: "1.3rem",
                      fontWeight: "800",
                      color: reward.color,
                      marginBottom: "5px",
                      letterSpacing: "-0.6px",
                    }}
                  >
                    {reward.tier}
                  </h3>
                  <p
                    style={{
                      color: "#64748B",
                      fontSize: "0.825rem",
                      marginBottom: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {reward.referrals} Referrals
                  </p>
                  <div
                    style={{
                      width: "40px",
                      height: "2px",
                      background: reward.color,
                      margin: "0 auto 12px",
                    }}
                  />
                  <p
                    style={{
                      color: "#2E2E2E",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      lineHeight: "1.4",
                    }}
                  >
                    {reward.reward}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        style={{
          background:
            "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
          padding: "40px 0",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2
              className="text-center mb-8"
              style={{
                fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
                fontWeight: "800",
                color: "#2E2E2E",
                letterSpacing: "-1px",
              }}
            >
              Why You'll Love It
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px 16px",
                    background: "white",
                    borderRadius: "10px",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "#22C55E",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FaCheckCircle
                      style={{ color: "white", fontSize: "1.2rem" }}
                    />
                  </div>
                  <p
                    style={{
                      color: "#2E2E2E",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      margin: 0,
                      letterSpacing: "-0.3px",
                    }}
                  >
                    {benefit}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Share on Social */}
      <section className="py-10">
        <div className="container mx-auto px-4 text-center">
          <h2
            className="mb-3"
            style={{
              fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
              fontWeight: "800",
              color: "#2E2E2E",
              letterSpacing: "-1px",
            }}
          >
            Spread the Word
          </h2>
          <p
            style={{
              color: "#64748B",
              fontSize: "0.9rem",
              marginBottom: "24px",
              maxWidth: "450px",
              margin: "0 auto 28px",
            }}
          >
            Share your referral link on social media and watch your rewards grow
          </p>

          <div className="flex justify-center gap-3 flex-wrap">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${APP_URLS.REFER}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                background: "#1877F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "1.3rem",
                transition: "all 0.3s ease",
                textDecoration: "none",
                boxShadow: "0 3px 10px rgba(24, 119, 242, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-3px) scale(1.08)";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(24, 119, 242, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 3px 10px rgba(24, 119, 242, 0.3)";
              }}
            >
              <FaFacebookF />
            </a>

            <a
              href={`https://twitter.com/intent/tweet?url=${APP_URLS.REFER}&text=Join me on Trivana Hospitality!`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                background: "#1DA1F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "1.3rem",
                transition: "all 0.3s ease",
                textDecoration: "none",
                boxShadow: "0 3px 10px rgba(29, 161, 242, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-3px) scale(1.08)";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(29, 161, 242, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 3px 10px rgba(29, 161, 242, 0.3)";
              }}
            >
              <FaTwitter />
            </a>

            <a
              href={`whatsapp://send?text=Check out Trivana Hospitality! Use my link: ${APP_URLS.REFER}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                background: "#25D366",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "1.3rem",
                transition: "all 0.3s ease",
                textDecoration: "none",
                boxShadow: "0 3px 10px rgba(37, 211, 102, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-3px) scale(1.08)";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(37, 211, 102, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 3px 10px rgba(37, 211, 102, 0.3)";
              }}
            >
              <FaWhatsapp />
            </a>

            <a
              href={`mailto:?subject=Join Trivana Hospitality!&body=Use my referral link: ${APP_URLS.REFER}`}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                background: "#EA4335",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "1.3rem",
                transition: "all 0.3s ease",
                textDecoration: "none",
                boxShadow: "0 3px 10px rgba(234, 67, 53, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-3px) scale(1.08)";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(234, 67, 53, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 3px 10px rgba(234, 67, 53, 0.3)";
              }}
            >
              <FaEnvelope />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
          padding: "45px 0",
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div
            style={{
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <h2
              style={{
                fontSize: "clamp(1.5rem, 3.5vw, 2rem)",
                fontWeight: "800",
                marginBottom: "10px",
                letterSpacing: "-1px",
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              }}
            >
              Start Earning Today
            </h2>
            <p
              style={{
                fontSize: "1rem",
                opacity: 0.95,
                marginBottom: "24px",
                fontWeight: "500",
                lineHeight: "1.5",
              }}
            >
              Copy your link, share with friends, and watch the rewards roll in
            </p>
            <button
              onClick={() => safeScrollToTop({ top: 0, behavior: "smooth" })}
              style={{
                padding: "13px 36px",
                background: "white",
                color: "#EE2E24",
                border: "none",
                borderRadius: "10px",
                fontSize: "1rem",
                fontWeight: "700",
                cursor: "pointer",
                boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                transition: "all 0.3s ease",
                letterSpacing: "-0.3px",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-3px)";
                e.target.style.boxShadow = "0 10px 28px rgba(0,0,0,0.25)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 6px 20px rgba(0,0,0,0.2)";
              }}
            >
              Get My Referral Link
            </button>
          </div>
        </div>
      </section>

      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
    </div>
  );
};

export default ReferAndEarn;
